import { useId, useState } from "react";

type UseComboboxNavigationOptions = {
    itemCount: number;

    isOpen: boolean;

    onSelect: (index: number) => void;

    onEscape?: () => void;

    enableHomeEnd?: boolean;

    resetKey?: string;
};

export function useComboboxNavigation({
    itemCount,
    isOpen,
    onSelect,
    onEscape,
    enableHomeEnd = false,
    resetKey,
}: UseComboboxNavigationOptions) {
    const [highlight, setHighlight] = useState({ resetKey, index: -1 });
    const highlightedIndex =
        highlight.resetKey === resetKey ? highlight.index : -1;
    const listboxId = useId();

    const getOptionId = (index: number) => `${listboxId}-option-${index}`;

    const setHighlightedIndex = (
        next: number | ((previous: number) => number),
    ) => {
        const nextIndex =
            typeof next === "function" ? next(highlightedIndex) : next;
        setHighlight({ resetKey, index: nextIndex });
        if (nextIndex >= 0) {
            requestAnimationFrame(() => {
                document
                    .getElementById(getOptionId(nextIndex))
                    ?.scrollIntoView({ block: "nearest" });
            });
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        switch (event.key) {
            case "ArrowDown": {
                event.preventDefault();
                if (itemCount === 0) return;
                setHighlightedIndex((prev) =>
                    prev < itemCount - 1 ? prev + 1 : 0,
                );
                break;
            }
            case "ArrowUp": {
                event.preventDefault();
                if (itemCount === 0) return;
                setHighlightedIndex((prev) =>
                    prev > 0 ? prev - 1 : itemCount - 1,
                );
                break;
            }
            case "Enter": {
                if (highlightedIndex >= 0 && highlightedIndex < itemCount) {
                    event.preventDefault();
                    onSelect(highlightedIndex);
                }
                break;
            }
            case "Escape": {
                if (onEscape) {
                    event.preventDefault();
                    onEscape();
                }
                break;
            }
            case "Home": {
                if (enableHomeEnd && itemCount > 0 && highlightedIndex >= 0) {
                    event.preventDefault();
                    setHighlightedIndex(0);
                }
                break;
            }
            case "End": {
                if (enableHomeEnd && itemCount > 0 && highlightedIndex >= 0) {
                    event.preventDefault();
                    setHighlightedIndex(itemCount - 1);
                }
                break;
            }
        }
    };

    const resetHighlight = () => setHighlightedIndex(-1);

    const inputProps = {
        role: "combobox" as const,
        "aria-expanded": isOpen,
        "aria-controls": isOpen ? listboxId : undefined,
        "aria-activedescendant":
            highlightedIndex >= 0 ? getOptionId(highlightedIndex) : undefined,
        "aria-autocomplete": "list" as const,
        "aria-haspopup": "listbox" as const,
    };

    const listboxProps = {
        role: "listbox" as const,
        id: listboxId,
    };

    const getOptionProps = (index: number) => ({
        id: getOptionId(index),
        role: "option" as const,

        tabIndex: -1,
        onMouseEnter: () => setHighlightedIndex(index),
        onMouseLeave: resetHighlight,
    });

    return {
        highlightedIndex,
        setHighlightedIndex,
        resetHighlight,
        getOptionId,
        handleKeyDown,
        inputProps,
        listboxProps,
        getOptionProps,
    };
}
